import Alpine from "alpinejs";

import { createUploaderStore } from "./uploader";
import { registerUploadGuards } from "./upload-guard";
import { registerBuildSkewGuard } from "./build-skew-guard";
import { registerNavProgress } from "./nav-progress";
import { registerPricingAutosaveGuard } from "./pricing-autosave-guard";
import { createSelectionStore } from "./selection";
import {
  fetchWithOOB,
  processOOBSwaps,
  registerGlobalHTMXListeners,
  dispatchToast,
  setupCSRF,
} from "./htmx-helpers";
import { registerFormBuilder } from "./form-builder";
import { registerAvatarUpload } from "./avatar-uploader";
import { registerAddonImageUpload } from "./addon-image-upload";
import { registerDeliverableManager } from "./deliverable-manager";
import { registerSelectionManager } from "./selection-manager";
import { resolveOrgSidebarSection } from "./org-sidebar-section";
import { flatpickrConfig, syncPickerToModel } from "./flatpickr-config";
import { registerDuplicateModal } from "./duplicate-modal";
import { registerReplaceModal } from "./replace-modal";
import { registerAppointments } from "./appointments";
import { registerProjectDetail } from "./project-detail";
import { registerTaskTemplatePrefiller } from "./task-template-prefiller";
import { registerAddressSearch } from "./address-search";
import { alpineData } from "./alpine-data";
import {
  formatEuros,
  feeSummary,
  taxRateValue,
  taxSummary,
  discountSummary,
  combinedEffectSummary,
  conditionsSummary,
  paymentSummaries,
  cancellationSummary,
  stayRulesSummary,
  stayRulesGapHint,
  stayRulesCutoffSentence,
  type SummaryCtx,
} from "./pricing-summary";
import { pricingGroupNeighbor } from "./pricing-group-neighbor";
import { occupancyExample, pricingOccupancy, type OccCtx } from "./pricing-occupancy";
import { createPricingLab } from "./pricing-lab";
import * as taxBrackets from "./pricing-tax-brackets";
import * as taxVersions from "./pricing-tax-versions";

export {
  fetchWithOOB,
  processOOBSwaps,
  registerGlobalHTMXListeners,
  dispatchToast,
  setupCSRF,
};

// Initialize Core Logic
// Searchable Select Component
Alpine.data(
  "searchableSelect",
  (options: { value: string; label: string }[], initialValue: string) => alpineData({
    open: false,
    search: "",
    value: initialValue,
    highlightedIndex: 0,
    options: options,

    // Computed: Filtered Options
    get filteredOptions() {
      if (!this.options || !Array.isArray(this.options)) return [];
      if (this.search === "") return this.options;
      return this.options.filter((opt) =>
        opt.label.toLowerCase().includes(this.search.toLowerCase()),
      );
    },

    // Computed: Valid selection label
    get currentLabel() {
      const opt = this.options.find((o) => o.value === this.value);
      return opt ? opt.label : "";
    },

    init() {
      this.$watch("open", (value: boolean) => {
        if (value) {
          this.search = "";
          this.highlightedIndex = 0;
          this.$nextTick(() => (this.$refs.searchInput as HTMLElement).focus());
        }
      });

      // Auto-reset when parent form resets
      const form = this.$el.closest("form");
      if (form) {
        form.addEventListener("reset", () => {
          this.value = initialValue;
          this.search = "";
        });
      }
    },

    select(val: string) {
      this.value = val;
      this.open = false;
      this.search = "";
      this.$nextTick(() => {
        this.$el.dispatchEvent(new Event("input", { bubbles: true }));
      });
    },

    isSelected(val: string) {
      return this.value === val;
    },
  }),
);

// MultiSelect Component
Alpine.data(
  "multiSelect",
  (options: { value: string; label: string }[], initialValues: string[]) => alpineData({
    open: false,
    selected: initialValues || [],
    options: options,

    init() {
      // Auto-reset when parent form resets
      const form = this.$el.closest("form");
      if (form) {
        form.addEventListener("reset", () => {
          this.selected = initialValues || [];
        });
      }
    },

    toggle(value: string) {
      if (this.selected.includes(value)) {
        this.selected = this.selected.filter((v) => v !== value);
      } else {
        this.selected.push(value);
      }
      this.dispatchChange();
    },
    remove(value: string) {
      this.selected = this.selected.filter((v) => v !== value);
      this.dispatchChange();
    },
    isSelected(value: string) {
      return this.selected.includes(value);
    },
    labels() {
      return this.selected.map((v) => {
        const o = this.options.find((opt) => opt.value === v);
        return o ? o.label : v;
      });
    },
    dispatchChange() {
      this.$dispatch("input", this.selected);
    },
  }),
);

// Shared config for both Flatpickr directives: page-language locale +
// optional data-min-date / data-max-date bounds (see flatpickr-config.ts).
const pickerConfig = (el: HTMLElement, enableTime: boolean) =>
  flatpickrConfig(el, {
    enableTime,
    lang: document.documentElement.lang,
    fp: flatpickr as any,
    appendTo: el.closest("dialog") || document.body,
  });

// DatePicker Directive (Flatpickr)
Alpine.directive("date-picker", (el, { expression }, { evaluateLater, effect }) => {
  const picker = flatpickr(el, pickerConfig(el, false));

  if (typeof expression === "string" && expression !== "true" && expression.trim() !== "") {
    const getValue = evaluateLater(expression);
    effect(() => {
      getValue((value: string) => syncPickerToModel(picker, value));
    });
  }

  picker.config.onChange.push((selectedDates: any, dateStr: string) => {
    // bubbles: true — a plain sample-stay date edit inside the pricing
    // preview drawer must reach htmx's `hx-trigger="change from:body"`
    // listener (property_pricing.templ's propertyPricingPreviewControl),
    // which only ever sees body-level bubbled events. Without this, a
    // Flatpickr date pick silently never refreshed the live preview (Task
    // 10 fix). A bubbling `change`/`input` is the standard DOM contract for
    // both event types (a native <input> always bubbles them) — this
    // directive was the outlier, not the other way around.
    el.dispatchEvent(new CustomEvent("input", { detail: dateStr, bubbles: true }));
    (el as HTMLInputElement).value = dateStr;
    el.dispatchEvent(new Event("change", { bubbles: true }));
  });

  const form = el.closest("form");
  if (form) {
    form.addEventListener("reset", () => {
      picker.clear();
    });
  }
});

// DateTimePicker Directive (Flatpickr)
Alpine.directive("datetime-picker", (el, { expression }, { evaluateLater, effect }) => {
  const picker = flatpickr(el, pickerConfig(el, true));

  if (typeof expression === "string" && expression !== "true" && expression.trim() !== "") {
    const getValue = evaluateLater(expression);
    effect(() => {
      getValue((value: string) => {
        if (value) picker.setDate(value, false);
      });
    });
  }

  picker.config.onChange.push((selectedDates: any, dateStr: string) => {
    el.dispatchEvent(new CustomEvent("input", { detail: dateStr }));
    (el as HTMLInputElement).value = dateStr;
    el.dispatchEvent(new Event("change"));
  });

  const form = el.closest("form");
  if (form) {
    form.addEventListener("reset", () => {
      picker.clear();
    });
  }
});

// TextArea Auto-Resize Directive
Alpine.directive("textarea-auto-resize", (el) => {
  const resize = () => {
    el.style.height = "auto";
    el.style.height = el.scrollHeight + "px";
  };

  el.addEventListener("input", resize);
  // Initial resize
  setTimeout(resize, 0);
});

// $formatEuros magic — delegates to pricing-summary.ts's formatEuros, the
// CLIENT-side twin of internal/money.FormatCents (Go); see that function's
// doc comment for the exact format and input tolerance. Kept as a magic for
// any pricing-surface x-text that needs a bare amount.
Alpine.magic("formatEuros", () => formatEuros);

// pricingGroupNeighbor: global for the fee list's group-aware reorder buttons
// (components.PricingRowReorderButtonsGrouped) — Pflichtgebühren and
// Zusatzangebote are the SAME `fees` array filtered by inclusion, so a move
// swaps with the nearest same-inclusion row instead of idx±1. Checked by
// scripts/verify-bundle-globals.mjs + studio-bundle.ts's self-check.
(window as any).pricingGroupNeighbor = pricingGroupNeighbor;

// pricingOccupancy: global for the "Belegung & Kinder" card's Alpine markup
// (row classification, tier/child-group editing, single-occupancy sign,
// turn-off). Checked by scripts/verify-bundle-globals.mjs + studio-bundle.ts's
// self-check.
(window as any).pricingOccupancy = pricingOccupancy;

// pricingLab: the price lab's Alpine component (Booking step 2). Keeps every
// sample input (guests, children, extras — a pet-scoped fee is just another
// extra, W2 — excluded rules, assumed rate) live across re-quotes; the
// server only re-renders the result and the config-derived inputs (which
// call syncMeta() back into this state).
Alpine.data("pricingLab", createPricingLab);

// $pricingSummary: plain-language sentences for pricing rows (Buchung step 1).
// Phrases come from the nearest [data-pricing-phrases] JSON (server-rendered
// via t(), so i18n stays in locale files); parsed once per element. Content
// teleported to <body> (the pricing edit drawers) has no such ancestor and
// falls back to the page's host.
const phraseCache = new WeakMap<Element, SummaryCtx["phrases"]>();
Alpine.magic("pricingSummary", (el: HTMLElement) => {
  const host = el.closest("[data-pricing-phrases]") || document.querySelector("[data-pricing-phrases]");
  let phrases = host ? phraseCache.get(host) : undefined;
  if (host && !phrases) {
    try {
      phrases = JSON.parse(host.getAttribute("data-pricing-phrases") || "{}");
    } catch {
      phrases = undefined;
    }
    if (phrases) phraseCache.set(host, phrases);
  }
  const withPhrases = (ctx: Omit<SummaryCtx, "phrases">): SummaryCtx => ({
    ...ctx,
    phrases: (phrases || {}) as SummaryCtx["phrases"],
  });
  return {
    fee: (row: any, ctx: Omit<SummaryCtx, "phrases">) => feeSummary(row, withPhrases(ctx)),
    tax: (row: any, ctx: Omit<SummaryCtx, "phrases">) => taxSummary(row, withPhrases(ctx)),
    taxValue: (row: any, ctx: Omit<SummaryCtx, "phrases">) => taxRateValue(row, withPhrases(ctx)),
    discount: (row: any, ctx: Omit<SummaryCtx, "phrases">) => discountSummary(row, withPhrases(ctx)),
    combined: (v: any, ctx: Omit<SummaryCtx, "phrases">) => combinedEffectSummary(v, withPhrases(ctx)),
    // conditions: the shared discount/coupon conditions block's one-liner.
    conditions: (row: any, ctx?: any) => conditionsSummary(row, withPhrases({ currency: "", ...(ctx || {}) })),
    payment: (p: any, ctx: Omit<SummaryCtx, "phrases">) => paymentSummaries(p, withPhrases(ctx)),
    occupancy: (rows: any[], ctx: Omit<OccCtx, "phrases">) => occupancyExample(rows, { ...ctx, phrases: ((phrases || {}) as any).occ || {} }),
    // cancellation (Task 8) takes phrases directly, not a {currency, phrases}
    // ctx — the terms ladder has no money amounts, only percents/day counts.
    cancellation: (input: any) => cancellationSummary(input, (phrases || {}) as SummaryCtx["phrases"]),
    // stayRules (R8) — like cancellation, takes phrases directly (no money).
    stayRules: (input: any) => stayRulesSummary(input, (phrases || {}) as SummaryCtx["phrases"]),
    // R8 Phase 3 — advisory lines read the card's Alpine stayRules object.
    stayRulesGapHint: (input: any) => stayRulesGapHint(input, (phrases || {}) as SummaryCtx["phrases"]),
    stayRulesCutoffSentence: (input: any) => stayRulesCutoffSentence(input, (phrases || {}) as SummaryCtx["phrases"]),
  };
});

// $taxBrackets: client-side price-bracket table helpers (guards, tidy, the
// drawer's "Testen" probe). Mirrors internal/pricing/price_bracket.go.
Alpine.magic("taxBrackets", () => taxBrackets);
// $taxVersions: scheduled tourist-tax rate changes (labels, add/promote, seeding).
Alpine.magic("taxVersions", () => taxVersions);

// x-destroy Directive
// Runs its expression when the element is torn down (removed from the DOM by an
// HTMX swap / Alpine cleanup). Chart components (analytics + reviews dashboards)
// rely on this to call their destroy() — releasing the ApexCharts instance and
// its theme-change listener. Without this directive x-destroy was a silent
// no-op, so those teardowns never ran.
Alpine.directive("destroy", (el, { expression }, { evaluateLater, cleanup }) => {
  const run = evaluateLater(expression);
  cleanup(() => run());
});

// Global Settings Store
Alpine.store("settings", {
  theme: localStorage.getItem("theme") || document.cookie.match(/theme=([^;]+)/)?.[1] || "studio",
  get lightMode() {
    return this.theme === "studio-light";
  },
  get darkMode() {
    return this.theme === "studio";
  },
  toggleTheme() {
    this.theme = this.theme === "studio" ? "studio-light" : "studio";
    localStorage.setItem("theme", this.theme);
    document.cookie = `theme=${this.theme}; path=/; max-age=31536000; SameSite=Lax`;
    document.documentElement.setAttribute("data-theme", this.theme);
  },
  init() {
    document.documentElement.setAttribute("data-theme", this.theme);
  },
});

// Global Sidebar Store
Alpine.store("sidebar", {
  collapsed: document.cookie.includes("sidebar-collapsed=true"),
  currentPath: window.location.pathname,
  mobileMenuOpen: false,
  toggle() {
    this.collapsed = !this.collapsed;
    document.cookie = `sidebar-collapsed=${this.collapsed}; path=/; max-age=31536000; SameSite=Lax`;
    localStorage.setItem("sidebar-collapsed", String(this.collapsed));
  },
  updatePath() {
    this.currentPath = window.location.pathname;
  },
  openMobileMenu() {
    this.mobileMenuOpen = true;
  },
  closeMobileMenu() {
    this.mobileMenuOpen = false;
  },
});

// $ohSection(rootEl): the org sidebar's active section, derived reactively from
// $store.sidebar.currentPath. Reads data-oh-base / data-oh-sections off the
// sidebar root. Reading currentPath here makes the caller (an x-effect on the
// root) re-run on every navigation.
Alpine.magic("ohSection", () => (el: HTMLElement) => {
  const base = el.dataset.ohBase || "";
  let sections: string[] = [];
  try {
    sections = JSON.parse(el.dataset.ohSections || "[]");
  } catch {
    sections = [];
  }
  const path = (Alpine.store("sidebar") as any).currentPath as string;
  return resolveOrgSidebarSection(path, base, sections);
});

// Global Wizard-Assistant Store
// Drives the mobile-only off-canvas assistant rail on the website wizard.
// The rail is ONE DOM node (templates/pages/wizard.templ) that is a static
// side panel at lg+ and an off-canvas right drawer below lg — mirroring the
// global sidebar's single-node responsive pattern. The summon button lives in
// PageHeader (a separate subtree), so the open flag MUST be a store.
Alpine.store("wizardAssistant", {
  open: false,
  toggle() {
    this.open = !this.open;
  },
  close() {
    this.open = false;
  },
});

// Aggregate autosave state for the wizard header status indicator (#140).
// `pending` = number of fields with a save in flight; `errored` = number whose
// last save failed and hasn't since succeeded. Both are maintained by setMark()
// in wizard-autosave.ts — the single chokepoint every autosave factory funnels
// through — so the header pill can never drift from the per-field marks. The
// header shows "Wird gespeichert…" while pending>0, a red "Nicht gespeichert"
// while errored>0, else the green "Gespeichert" (safe-to-close) state.
Alpine.store("wizardSave", {
  pending: 0,
  errored: 0,
});

// Pins a fixed off-canvas rail element to the VisualViewport. On iOS the soft
// keyboard shrinks only the VISUAL viewport, not the layout viewport, so a fixed
// full-height rail overflows below the keyboard (a "white strip" appears on
// over-scroll) and its top — the close (X) button — scrolls out of reach. Sizing
// the rail to visualViewport.height and offsetTop keeps it exactly within the
// visible area: input above the keyboard, X reachable, no overflow strip. No-op
// at lg+ (the rail is a static side panel there) — inline styles are cleared.
//
// Returns a plain object (not an Alpine.data component) so callers can spread
// its `init` into their own x-data object — Alpine allows only one x-data per
// element, so a component that already needs other local state (e.g. the CMS
// assistant panel's tab/editsCount) can't just do `x-data="wizardRail"`; it
// has to merge this behavior in instead. `this` inside `init()` resolves to
// whichever Alpine component ends up owning the method, exactly as if it had
// been declared there directly.
function viewportPinnedRail() {
  return alpineData({
    init() {
      const el = this.$el as HTMLElement;
      const apply = () => {
        const vv = window.visualViewport;
        if (!vv || window.innerWidth >= 1024) {
          el.style.top = "";
          el.style.height = "";
          el.style.bottom = "";
          return;
        }
        el.style.top = `${vv.offsetTop}px`;
        el.style.height = `${vv.height}px`;
        el.style.bottom = "auto";
      };
      apply();
      if (window.visualViewport) {
        window.visualViewport.addEventListener("resize", apply);
        window.visualViewport.addEventListener("scroll", apply);
      }
      window.addEventListener("resize", apply);
    },
  });
}

// Mobile wizard assistant rail: no other local state, so it's just the pinning.
Alpine.data("wizardRail", () => ({
  ...viewportPinnedRail(),
}));

// CMS-native assistant off-canvas rail (Task 5, cms_assistant_panel.templ).
// The panel's <aside> already needs local `tab`/`editsCount` state (tab
// switcher + the "Änderungen · N" count pill, kept live by an
// @htmx:after-swap listener on the same element) — merging the SAME
// viewport-pinning behavior as wizardRail into one x-data object, rather than
// a second x-data, since Alpine only allows one per element and the pinning
// must run on this fixed <aside> (it sizes this.$el). `editsCount` is seeded
// server-side from the initial review-queue count.
Alpine.data("cmsAssistantRail", (editsCount: number) => ({
  tab: "chat",
  editsCount,
  ...viewportPinnedRail(),
}));

// What's New drawer (Task 9, components/whatsnew.templ). Auto-opens once on
// a fresh full page load when the viewer has unread updates (server-decided
// via `autoOpen`, threaded through x-data). No @alpinejs/focus plugin is
// registered in this bundle, so focus save/restore is handled manually here
// rather than via x-trap.
Alpine.data("whatsNewDrawer", (autoOpen: boolean) =>
  alpineData({
    isOpen: false,
    loaded: false,
    _prevFocus: null as HTMLElement | null,
    init() {
      if (autoOpen) {
        this.$nextTick(() => this.open());
      }
    },
    open() {
      this._prevFocus = document.activeElement as HTMLElement | null;
      this.isOpen = true;
      if (!this.loaded) {
        this.$el.querySelector("[hx-get]")?.dispatchEvent(new Event("whatsnew-load"));
        this.loaded = true;
      }
      this.$nextTick(() => {
        (this.$el.querySelector("aside button") as HTMLElement | null)?.focus();
      });
      // Mark seen + clear badge. htmx.ajax auto-attaches X-CSRF-Token via the
      // global htmx:configRequest listener (htmx-helpers.ts); the response's
      // OOB badge swap (sidebar + drawer) is processed by htmx as usual.
      (window as any).htmx.ajax("POST", "/api/whats-new/seen", { swap: "none" });
    },
    close() {
      this.isOpen = false;
      this._prevFocus?.focus?.();
    },
  }),
);

// Sync currentPath on navigation
document.addEventListener("htmx:afterOnLoad", () => {
  (Alpine.store("sidebar") as any).updatePath();
});
window.addEventListener("popstate", () => {
  (Alpine.store("sidebar") as any).updatePath();
});

// Global Property-Hub Store
// Tracks the active property-hub section. Read by the hub sidebar links
// (:class / @click) and the header "Add review" button (x-show); written by the
// sidebar's x-init from the server-rendered ActiveSection on each page load.
//
// This MUST be registered here — synchronously at module load, before
// Alpine.start() — and NOT in a per-page inline <script>. A prior inline
// registration raced Alpine's evaluation of those x-show/x-init expressions on
// hx-boost navigations (where alpine:init never re-fires), so $store.propertyHub
// was sometimes undefined when first read: "Cannot read/set properties of
// undefined (reading/setting 'section')" (GlitchTip STUDIO-BROWSER-P/Q, ~80
// events/day). Registering it centrally makes the store exist on every page and
// persist across boosted navigations, exactly like the sidebar store above, so
// the race cannot occur.
Alpine.store("propertyHub", {
  section: "",
});

// Global Pricing-Tabs Store
// Holds the pricing section's active tab (fees/taxes/discounts/payment),
// read/written by property_pricing.templ's #pricing-content computed
// `activeTab` property. That container self-refreshes (outerHTML swap) on
// every fees/taxes/discounts/payment save — a plain `x-data="{ activeTab:
// 'fees' }"` local var would be rebuilt from scratch by that swap, resetting
// the operator back to the Fees tab after every save regardless of which
// tab they were on. Registering the tab state here, same as the sidebar/
// propertyHub stores above, means it survives the swap: it's created once at
// module load and never touched by the content re-render.
Alpine.store("pricingTabs", {
  current: "fees",
});

// Global Uploader Store
Alpine.store("uploader", createUploaderStore(Alpine));

// Upload Guards (beforeunload + htmx:beforeOnLoad confirms during in-flight uploads)
registerUploadGuards(Alpine);

// Build-skew guard: a tab left open across a deploy hard-navigates (once) on the
// next boosted navigation instead of swapping new markup into the old bundle.
registerBuildSkewGuard(Alpine);

// Global navigation progress bar: thin copper bar at the top of the viewport for
// slow boosted/full-page navigations (+ Back/Forward refetches). Stands down
// while the CMS page-switch overlay ($store.cmsNav) is showing.
registerNavProgress(Alpine);

// Pricing autosave navigation guard (warns before leaving with unsaved/invalid
// pricing draft edits — the autosave twin of the upload guard above).
registerPricingAutosaveGuard();

// Global Selection Store
Alpine.store("selection", createSelectionStore(Alpine));

// Selection Delegation Directive (x-selection-delegate)
Alpine.directive("selection-delegate", (el) => {
  el.addEventListener("click", (e: Event) => {
    const mouseEvent = e as MouseEvent;
    const targetEl = mouseEvent.target as HTMLElement;

    // 1. Handle Batch Actions (Select All)
    const actionBtn = targetEl.closest("[data-selection-action]");
    if (actionBtn) {
      const action = (actionBtn as HTMLElement).dataset.selectionAction;
      if (action === "select-all") {
        // Find all assets within the same context (e.g., the same group section)
        const context = actionBtn.closest("section") || el;
        const assetIds = Array.from(context.querySelectorAll("[data-asset-id]"))
          .map((node) => (node as HTMLElement).dataset.assetId || "")
          .filter(Boolean);

        Alpine.store("selection").selectAll(assetIds);
        return;
      }
    }

    // 2. Handle Individual Asset Clicks
    const target = targetEl.closest("[data-asset-id]");
    if (!target) return;

    const id = (target as HTMLElement).dataset.assetId;
    if (id) {
      // Get all IDs in visual order for Shift+Click range (usually scoped to the whole delegate container)
      const allIds = Array.from(el.querySelectorAll("[data-asset-id]"))
        .map((node) => (node as HTMLElement).dataset.assetId || "")
        .filter(Boolean);

      Alpine.store("selection").toggle(id, mouseEvent.shiftKey, allIds);
    }
  });
});

// Basic Form Dirty State Tracker
Alpine.data("formDirtyTracker", () => alpineData({
  isDirty: false,
  initialData: "",

  init() {
    this.initialData = this.serialize();

    const check = () => {
      this.isDirty = this.serialize() !== this.initialData;
    };

    this.$el.addEventListener("input", check);
    this.$el.addEventListener("change", check);

    // Reset state after successful HTMX swap/update
    this.$el.addEventListener("htmx:afterOnLoad", (e: any) => {
      if (e.detail.xhr.status >= 200 && e.detail.xhr.status < 300) {
        this.initialData = this.serialize();
        this.isDirty = false;
      }
    });
  },

  serialize() {
    const formData = new FormData(this.$el as HTMLFormElement);
    return new URLSearchParams(formData as any).toString();
  },
}));

// Form Builder
registerFormBuilder(Alpine);

// Avatar Upload
registerAvatarUpload(Alpine);
registerAddonImageUpload(Alpine);

// Deliverable Manager
registerDeliverableManager(Alpine);

// Selection Manager (selection-gallery manage-page sort mode — reorder only)
registerSelectionManager(Alpine);

// Duplicate Group Modal
registerDuplicateModal(Alpine);

// Replace Asset Modal
registerReplaceModal(Alpine);

// Appointments
registerAppointments(Alpine);

// Project Detail
registerProjectDetail(Alpine);

// Task Template Prefiller
registerTaskTemplatePrefiller(Alpine);

// Address Search (remote geocode autocomplete combobox)
registerAddressSearch(Alpine);
